import { Fragment, forwardRef, useMemo, useRef, useState } from "react";

import {
  ModelSearchResultFragment,
  SearchSyncDestinationsResult,
  SourceType,
  useGetRawViewsQuery,
  useMostRecentPublishedModelsQuery,
} from "@/apollo/types";
import { Badge } from "@/components/elements/Badge";
import { Button } from "@/components/elements/Button";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import Tooltip from "@/components/elements/Tooltip";
import ImportIcon from "@/components/icons/ImportIcon";
import { formatModelName } from "@/features/models";
import cn from "@/helpers/classNames";
import { useMountEffect } from "@/hooks/useMountEffect";
import { IntegrationLogo, IntegrationLogoBox } from "@/integrations";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { Combobox, ComboboxProps, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";

import { Source } from "../../form";
import { useSourceSearch } from "../../lib/useSourceSearch";

type ModelOption = Source & {
  data?: ModelSearchResultFragment;
  type: SourceType.Model;
};

type ViewOption = Source & {
  data?: SearchSyncDestinationsResult;
  type: SourceType.RawView;
};

export type SourceOption = ModelOption | ViewOption;

type SourceSelectorProps = ComboboxProps<SourceOption, true, false, "div">;

function useImportedRawViewsOptions(query: string): ViewOption[] {
  const { data: rawViewData } = useGetRawViewsQuery();
  const options = useMemo(() => {
    if (!rawViewData?.getRawViews) {
      return [];
    }
    const importedViews = rawViewData.getRawViews.filter(
      (view) => view.syncId == null,
    );
    return importedViews.map(
      (view): ViewOption => ({
        label: `${view.schema}.${view.sourceStream}`,
        value: view.viewId,
        type: SourceType.RawView,
        data: undefined,
      }),
    );
  }, [rawViewData]);

  return useMemo(() => {
    if (query.trim().length === 0) {
      return options;
    }
    const trimmedQuery = query.trim().replace(/[\s.]+$/, ""); // Remove trailing spaces and dots
    return options.filter((option) =>
      option.label.toLowerCase().includes(trimmedQuery.toLowerCase()),
    );
  }, [options, query]);
}

function useRecentlyPublishedModelOptions(query: string): ModelOption[] {
  const { data: recentlyPublishedModelsData } =
    useMostRecentPublishedModelsQuery();
  const options = useMemo(() => {
    if (!recentlyPublishedModelsData?.mostRecentPublishedModels) {
      return [];
    }
    return recentlyPublishedModelsData.mostRecentPublishedModels.map(
      (model): ModelOption => ({
        label: formatModelName(model),
        value: model.id,
        type: SourceType.Model,
        data: model,
      }),
    );
  }, [recentlyPublishedModelsData]);

  return useMemo(() => {
    if (query.trim().length === 0) {
      return options;
    }
    const trimmedQuery = query.trim().replace(/[\s.]+$/, ""); // Remove trailing spaces and dots
    return options.filter((option) =>
      option.label.toLowerCase().includes(trimmedQuery.toLowerCase()),
    );
  }, [options, query]);
}

export const SourceSelector = forwardRef<
  HTMLDivElement,
  SourceSelectorProps & {
    defaultOpen?: boolean;
    inputId?: string;
    onImportClick?: () => void;
  }
>(({ defaultOpen, inputId, onImportClick, ...props }, ref) => {
  const [query, setQuery] = useState("");

  // Search for models and views (active in data source syncs)
  const { options, isSearching } = useSourceSearch(query);
  // Include imported views, i.e. views that are not part of the sync
  const importedViewOptions = useImportedRawViewsOptions(query);
  // Include recently published models
  const recentlyPublishedModelOptions = useRecentlyPublishedModelOptions(query);

  const dwh = useDataWarehouseContext();

  const inputRef = useRef<HTMLInputElement>(null);

  const forceOpenDropdown = () => {
    // The Combobox does not have a programmatic way to open the dropdown
    // We can force it by dispatching a keyboard event (alternatively, by simulating a click on the dropdown button)
    if (inputRef.current) {
      inputRef.current.focus();
      const event = new KeyboardEvent("keydown", {
        key: "ArrowDown",
        code: "ArrowDown",
        keyCode: 40,
        which: 40,
        bubbles: true,
      });
      inputRef.current.dispatchEvent(event);
    }
  };

  useMountEffect(() => {
    setTimeout(() => {
      if (defaultOpen) {
        forceOpenDropdown();
      }
    }, 400);
  });

  const isOpenRef = useRef(false);

  return (
    <Combobox<SourceOption, "div">
      as="div"
      ref={ref}
      by="value"
      {...props}
      onChange={(value) => {
        props.onChange?.(value);
      }}
      onClick={() => forceOpenDropdown()}
    >
      {({ open }) => {
        if (open !== isOpenRef.current) {
          isOpenRef.current = open;
          if (open) {
            // Select the entire input text when dropdown opens
            inputRef.current?.select();
          }
        }

        return (
          <div className="relative">
            <div className="relative flex h-11 w-full cursor-default items-center overflow-hidden rounded-sm border text-left text-sm focus-within:outline focus-within:outline-blue-600">
              <IntegrationLogoBox
                id={dwh.integration.id}
                size="xs"
                className="ml-2 h-8 w-8"
              />
              <Combobox.Input
                className="h-full w-fit flex-1 truncate border-none bg-transparent py-2 pl-2 pr-2 text-sm leading-5 text-foreground focus:outline-none focus:ring-0"
                displayValue={(o: SourceOption | undefined) => o?.label || ""}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                placeholder="Search for a model or view..."
                ref={inputRef}
                id={inputId}
              />
              <div className="ml-auto flex items-center gap-2 pr-2">
                {props.value && (
                  <div className="flex items-center gap-2">
                    {props.value.type === SourceType.RawView &&
                      props.value.data?.integrationId && (
                        <IntegrationLogoBox
                          id={props.value.data.integrationId}
                          className="h-5 w-5"
                        />
                      )}
                    <Badge
                      variant="secondary"
                      className="px-0.5 py-0 uppercase leading-tight"
                    >
                      {props.value.type === SourceType.RawView
                        ? "View"
                        : "Model"}
                    </Badge>
                  </div>
                )}
                <Combobox.Button className="">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-muted-foreground"
                    aria-hidden="true"
                  />
                </Combobox.Button>
              </div>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options
                as="div"
                className="absolute z-10 mt-1 h-72 w-full overflow-hidden rounded-sm bg-background text-base text-foreground shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-900"
              >
                <div className="flex h-full flex-col">
                  <div className="flex-1 space-y-1 overflow-hidden text-sm">
                    <div className="h-full overflow-auto">
                      {query.trim().length === 0 ? (
                        <div className="relative cursor-default select-none px-4 py-4 text-muted-foreground">
                          <div className="flex flex-col items-center">
                            <em>
                              Search for a model or view, or pick from recently
                              published models below.
                            </em>
                          </div>
                        </div>
                      ) : isSearching ? (
                        <div className="relative flex cursor-default select-none items-center px-4 py-2 text-muted-foreground">
                          <LoadingSpinner className="mr-3 h-4 w-4" />
                          Searching...
                        </div>
                      ) : options.length === 0 ? (
                        <Transition
                          appear
                          show={true}
                          enter="transition-opacity duration-300 delay-300"
                          enterFrom="opacity-0"
                          enterTo="opacity-100"
                        >
                          <div className="relative cursor-default select-none px-4 py-2 text-muted-foreground">
                            No models or views found.
                          </div>
                        </Transition>
                      ) : null}
                      {options.length > 0 && (
                        <ul className="py-1">
                          {options.map((option) => {
                            return (
                              <Combobox.Option
                                key={option.value}
                                value={option}
                                title={option.label}
                                className={({ active }) =>
                                  cn(
                                    `relative cursor-default select-none truncate px-4 py-2`,
                                    {
                                      "bg-blue-500 text-white": active,
                                    },
                                  )
                                }
                              >
                                {({ selected }) => (
                                  <div className="flex gap-2">
                                    <Tooltip content={option.label}>
                                      <div
                                        className={cn("truncate", {
                                          "font-medium": selected,
                                        })}
                                      >
                                        {option.label}
                                      </div>
                                    </Tooltip>
                                    <div className="ml-auto flex items-center gap-2">
                                      {option.type === SourceType.RawView &&
                                        option.data && (
                                          <IntegrationLogoBox
                                            id={option.data.integrationId}
                                            className="h-5 w-5"
                                          />
                                        )}
                                      <Badge
                                        variant="secondary"
                                        className="px-0.5 py-0 uppercase leading-tight"
                                      >
                                        {option.type === SourceType.RawView
                                          ? "View"
                                          : "Model"}
                                      </Badge>
                                    </div>
                                  </div>
                                )}
                              </Combobox.Option>
                            );
                          })}
                        </ul>
                      )}
                      {recentlyPublishedModelOptions.length > 0 && (
                        <>
                          <div className="cursor-default select-none border-t px-4 pb-1.5 pt-3 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Recently Published Models
                          </div>
                          <ul className="py-1">
                            {recentlyPublishedModelOptions.map((option) => {
                              return (
                                <Combobox.Option
                                  key={option.value}
                                  value={option}
                                  title={option.label}
                                  className={({ active }) =>
                                    cn(
                                      `relative cursor-default select-none truncate px-4 py-2`,
                                      {
                                        "bg-blue-500 text-white": active,
                                      },
                                    )
                                  }
                                >
                                  {option.label}
                                </Combobox.Option>
                              );
                            })}
                          </ul>
                        </>
                      )}
                      {importedViewOptions.length > 0 && (
                        <>
                          <div className="cursor-default select-none border-t px-4 pb-1.5 pt-3 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Imported Views
                          </div>
                          <ul className="py-1">
                            {importedViewOptions.map((option) => {
                              return (
                                <Combobox.Option
                                  key={option.value}
                                  value={option}
                                  title={option.label}
                                  className={({ active }) =>
                                    cn(
                                      `relative cursor-default select-none truncate px-4 py-2`,
                                      {
                                        "bg-blue-500 text-white": active,
                                      },
                                    )
                                  }
                                >
                                  {option.label}
                                </Combobox.Option>
                              );
                            })}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-none justify-end border-t px-4 py-2 text-xs">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-foreground/80"
                      icon={<ImportIcon />}
                      iconRight={<IntegrationLogo id={dwh.integration.id} />}
                      onClick={onImportClick}
                    >
                      Import view from {dwh.integration.name}
                    </Button>
                  </div>
                </div>
              </Combobox.Options>
            </Transition>
          </div>
        );
      }}
    </Combobox>
  );
});
